// ConsentGate.tsx — the gentle, two-step welcome shown once after sign-in, before
// the app opens. Warm and spacious (a survivor's first arrival), but the research
// terms are shown IN FULL on step 2 — informed consent means nothing is hidden.
//   Step 1 — a warm welcome + a light, plain agreement to Terms & Privacy (REQUIRED).
//   Step 2 — a kind, OPTIONAL invitation to help with the research, listing exactly
//            what taking part means, with the full notice one link away.
// Each decision is written to local state (drives the app, works offline) and logged
// to Supabase via services/consent (the demonstrable record). Setting the research
// decision clears the gate (App re-renders past it).
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { Icon, type IconName, Wordmark } from '@/components/icons';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/hooks/useAuth';
import { saveUserType } from '@/services/auth';
import { logConsent } from '@/services/consent';
import { canSync, postResearchConsent } from '@/services/sync';
import { ResearchConsentStep } from './ResearchConsentStep';
import { CONSENT_VERSIONS, TERMS_URL, PRIVACY_URL } from '@/config/consent';
import { openExternal } from '@/services/openExternal';

const openUrl = (url: string) => openExternal(url);

const inlineLink: React.CSSProperties = {
  background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit',
  color: 'var(--amber-deep)', fontWeight: 600, textDecoration: 'underline',
};

/** A soft circular icon badge — the bit of warmth that replaces a wall of text. */
function Badge({ name }: { name: IconName }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 66, height: 66, borderRadius: 999, marginBottom: 18,
        background: 'rgba(200,134,26,0.13)', color: 'var(--amber-deep)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Icon name={name} size={30} />
    </div>
  );
}

/** Calm step dots so it feels short and finite. */
function StepDots({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div style={{ display: 'flex', gap: 7, margin: '14px 0 4px' }}>
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          style={{
            width: n === step ? 22 : 8, height: 8, borderRadius: 999,
            background: n === step ? 'var(--amber-deep)' : 'var(--hair-strong)',
            transition: 'width .25s, background .25s',
          }}
        />
      ))}
    </div>
  );
}

function Frame({ step, children }: { step: 1 | 2 | 3; children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'relative', width: '100%', height: '100dvh', maxWidth: 520, margin: '0 auto',
        background: 'var(--cream)', display: 'flex', flexDirection: 'column',
        // This screen is the sole scroller; contain its overscroll so reaching the
        // top/bottom never chains out to the locked document (which would swipe the
        // whole app on mobile web). Matches AppShell's data-scroll-area.
        overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
      }}
    >
      <div
        style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          padding: 'calc(26px + var(--safe-top)) 26px calc(30px + var(--safe-bottom))',
        }}
      >
        {/* alignSelf keeps the logo at its natural size — without it the flex column
            stretches the <img> to full width and distorts it. */}
        <div style={{ alignSelf: 'flex-start' }}><Wordmark h={20} /></div>
        <StepDots step={step} />
        {children}
      </div>
    </div>
  );
}

export function ConsentGate() {
  const progress = useProgress();
  const auth = useAuth();
  // Resume at the right step so an existing user isn't re-asked things they've done.
  const [step, setStep] = React.useState<1 | 2 | 3>(() =>
    progress.state.termsAcceptedVersion !== CONSENT_VERSIONS.terms ? 1 : progress.state.userType == null ? 2 : 3,
  );
  const [saving, setSaving] = React.useState(false);

  const agreeAndContinue = () => {
    progress.acceptTerms(CONSENT_VERSIONS.terms);
    void logConsent('terms', 'accepted', CONSENT_VERSIONS.terms);
    setStep(2);
  };

  // Who is this? Chosen once, written to the profile (which gates the research PID).
  // Only a survivor continues to the research-consent step; everyone else gets the
  // full app but skips research entirely (auto-declined here so the gate clears).
  const chooseType = (type: 'survivor' | 'curious' | 'staff' | 'other') => {
    if (saving) return;
    progress.setUserType(type);
    void saveUserType(type);
    if (type === 'survivor') { setStep(3); return; }
    progress.setResearchConsent('declined', CONSENT_VERSIONS.research);
    void logConsent('research', 'declined', CONSENT_VERSIONS.research);
  };

  const finish = (granted: boolean) => {
    if (saving) return;
    setSaving(true);
    const decision: 'granted' | 'declined' = granted ? 'granted' : 'declined';
    progress.setResearchConsent(decision, CONSENT_VERSIONS.research);
    void logConsent('research', decision, CONSENT_VERSIONS.research);
    // Mirror the decision to the research backend so its record matches (best-effort).
    const s = auth.session;
    if (canSync(s) && s.accessToken) void postResearchConsent(s.accessToken, granted);
    // termsAcceptedVersion is set (step 1) and researchConsent is no longer 'unset',
    // so the gate clears on the next render and the app opens.
  };

  if (step === 1) {
    return (
      <Frame step={1}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', padding: '18px 0 4px' }}>
          <Badge name="heart" />
          <Display text="We’re really glad you’re here." size={27} weight={400} lh={1.16} style={{ marginBottom: 12 }} />
          <Body size={15.5} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 18, maxWidth: '32ch' }}>
            A calm, private space to heal at your own pace. Before you start, a few things to understand and agree to.
          </Body>
          <div style={{ background: 'var(--card-top)', border: '1px solid var(--hair)', borderRadius: 16, padding: '2px 16px' }}>
            {([
              ['Educational, not clinical care.', 'ReNurture is a self-guided recovery programme, not a substitute for therapy, counselling or medical care.'],
              ['Your privacy comes first.', 'Anything you write stays on your own device, and you can leave at any moment.'],
              ['Not a crisis service.', 'If you are in immediate danger, please call 999.'],
            ] as const).map(([t, b], i, arr) => (
              <div key={i} style={{ padding: '13px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--hair)' : 'none' }}>
                <Body size={13.5} style={{ lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{t}</strong> {b}
                </Body>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 18 }}>
          <Btn full size="lg" onClick={agreeAndContinue}>I understand and agree</Btn>
          <Body size={12.5} style={{ textAlign: 'center', color: 'var(--ink-soft)', marginTop: 11, lineHeight: 1.5 }}>
            You also agree to our{' '}
            <button type="button" style={inlineLink} onClick={() => openUrl(TERMS_URL)}>Terms</button>{' '}and{' '}
            <button type="button" style={inlineLink} onClick={() => openUrl(PRIVACY_URL)}>Privacy Policy</button>.
          </Body>
        </div>
      </Frame>
    );
  }

  if (step === 2) {
    const OPTIONS: { type: 'survivor' | 'curious' | 'staff' | 'other'; label: string }[] = [
      { type: 'survivor', label: 'I’m here for my own recovery' },
      { type: 'curious', label: 'I’m exploring out of interest' },
      { type: 'staff', label: 'I’m a professional or staff member looking around' },
      { type: 'other', label: 'Something else' },
    ];
    return (
      <Frame step={2}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', padding: '18px 0 4px' }}>
          <Badge name="you" />
          <Display text="Which best describes you?" size={27} weight={400} lh={1.16} style={{ marginBottom: 12 }} />
          <Body size={15.5} style={{ lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 18, maxWidth: '34ch' }}>
            This helps us tailor the experience, and means our research only ever includes the people the programme is for. Please choose what fits best — this one can’t be changed later.
          </Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {OPTIONS.map((o) => (
              <button
                key={o.type}
                type="button"
                disabled={saving}
                onClick={() => chooseType(o.type)}
                style={{
                  textAlign: 'left', padding: '15px 17px', borderRadius: 14,
                  border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)',
                  font: 'inherit', fontSize: 15, fontWeight: 500, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                }}
              >
                {o.label}
                <Icon name="chevRight" size={16} />
              </button>
            ))}
          </div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame step={3}>
      <ResearchConsentStep onDecide={finish} saving={saving} />
    </Frame>
  );
}
